import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";

import { TicketFilter } from "./ticketFilter";
import { createTicketFilter, deleteTicketFilter, fetchTicketFilters, updateTicketFilter } from "./ticketFilterApi";
import TicketFilterForm from "./TicketFiltersForm";
import NoResultsFound from "../../common/components/noResultsFound/NoResultsFound";
import { LS_LAST_USED_TICKET_FILTER_ID } from "../../common/utils/common";

export default function TicketFiltersPage() {
  const [showTicketFilterDialog, setShowTicketFilterDialog] = useState(false);
  const [ticketFilters, setTicketFilters] = useState<TicketFilter[]>();
  const [selectedTicketFilter, setSelectedTicketFilter] = useState<TicketFilter | undefined>(undefined);

  useEffect(() => {
    doFetchFilters();
  }, []);

  const doFetchFilters = async () => {
    const filters = await fetchTicketFilters();
    setTicketFilters(filters);
  };

  const closeDialog = () => {
    setSelectedTicketFilter(undefined);
    setShowTicketFilterDialog(false);
  };

  const onEdit = (ticketFilter: TicketFilter) => {
    setSelectedTicketFilter(ticketFilter);
    setShowTicketFilterDialog(true);
  };

  const onDelete = (ticketFilter: TicketFilter) => {
    confirmDialog({
      header: "Confirmare stergere",
      icon: "pi pi-exclamation-triangle",
      message: (
        <div className="text-xl">
          Esti sigur ca vrei sa stergi filtrul <span className="font-bold">{ticketFilter.name}</span>?
        </div>
      ),
      acceptLabel: "Da",
      rejectLabel: "Nu",
      accept: async () => {
        await deleteTicketFilter(ticketFilter);
        await doFetchFilters();
      },
    });
  };

  const onSubmit = async (ticketFilter: TicketFilter) => {
    const isNameDuplicated = ticketFilters?.some(
      (f) => f.name.toLowerCase() === ticketFilter.name.toLowerCase() && f.id !== ticketFilter.id
    );
    if (isNameDuplicated) {
      toast.error("Există deja un filtru cu acest nume.");
      closeDialog();
      return;
    }

    if (selectedTicketFilter) {
      await updateTicketFilter(ticketFilter);
      setSelectedTicketFilter(undefined);
    } else {
      await createTicketFilter(ticketFilter);
    }

    const cachedFilterId = localStorage.getItem(LS_LAST_USED_TICKET_FILTER_ID);
    if (ticketFilter.id === cachedFilterId) {
      localStorage.removeItem(LS_LAST_USED_TICKET_FILTER_ID);
    }

    await doFetchFilters();
    closeDialog();
  };

  return (
    <Card
      title={
        <div className="flex align-items-center justify-content-between">
          <div className="text-2xl font-bold">Filtrele mele</div>
          <Button label="Adauga" icon="pi pi-plus" onClick={() => setShowTicketFilterDialog(true)} />
        </div>
      }
    >
      <DataTable size="small" value={ticketFilters} emptyMessage={<NoResultsFound />}>
        <Column field="name" header="Nume"></Column>

        <Column
          className="w-8rem"
          body={(rowData: TicketFilter) => (
            <div className="flex align-items-center justify-content-end gap-2">
              <Button size="small" label="Editeaza" icon="pi pi-pencil" onClick={() => onEdit(rowData)} />
              <Button size="small" label="Sterge" severity="danger" icon="pi pi-trash" onClick={() => onDelete(rowData)} />
            </div>
          )}
        ></Column>
      </DataTable>
      <Dialog
        className="w-almost-full md:w-8"
        header={selectedTicketFilter ? "Editeaza filtru" : "Adauga filtru"}
        visible={showTicketFilterDialog}
        onHide={() => closeDialog()}
      >
        <TicketFilterForm filter={selectedTicketFilter} onSubmit={onSubmit} />
      </Dialog>
    </Card>
  );
}
